import {
  TFormatNumberSuffix,
  formatNumberForTable,
  formatWeight,
} from "@/Utils/formatFunctions";
import { Checkbox } from "@/components";
import { ShareLink } from "@/components/ShareLink";
import UnitDiff from "@/components/UnitDiff";
import { translate } from "@/translations/functions/hook";

import { twMerge } from "tailwind-merge";

import { getTenantUrl } from "../getTenantUrl";

export enum DeltaTrend {
  Positive = "positive",
  Negative = "negative",
  Neutral = "neutral",
}

type onChangeType = (value: any, values: any) => void;

export type ColumnType = {
  label: string;
  name: string;
  getView: (
    value: any,
    values?: any,
    onChange?: onChangeType,
  ) => React.JSX.Element;
  headerClassNames?: string;
  sortable?: boolean;
  headerTooltipText?: string;
  headerTooltipComponent?: React.ReactNode;
  onChange?: onChangeType;
};

export const RIGHT_ALIGN_CLASS = "justify-end text-end pr-8";

export const titleView = (value: string) => (
  <span className="block overflow-hidden pl-3 text-start text-sm leading-[48px] text-ellipsis whitespace-nowrap text-black">
    {value}
  </span>
);

export const titleViewWithLink = (
  value: string,
  rowData: { [k: string]: any },
) => (
  <div className="flex items-center">
    <span className="block overflow-hidden pl-3 text-start text-sm leading-[48px] text-ellipsis whitespace-nowrap text-black">
      {value}
    </span>
    {rowData.tenantCode && (
      <ShareLink link={getTenantUrl(rowData.tenantCode)} />
    )}
  </div>
);

export const titleViewClickable = (
  value: string,
  rowData: { [k: string]: any },
) => (
  <div className="flex items-center">
    <span className="block overflow-hidden pl-3 text-start text-sm leading-[48px] text-ellipsis whitespace-nowrap text-black underline">
      <a
        href={getTenantUrl(rowData.tenantCode)}
        target="_blank"
        rel="noreferrer"
      >
        {value}
      </a>
    </span>
  </div>
);

export const percentageView = (
  value: string,
  suffix: TFormatNumberSuffix,
  cellClassNames: string = "",
) => (
  <span
    className={twMerge(
      "flex items-center justify-start pl-3 text-sm text-black",
      cellClassNames,
    )}
  >
    {formatNumberForTable(value, { suffix })}
  </span>
);

const getChangeValue = (value: number) => {
  if (value < 0) return DeltaTrend.Negative;

  if (value === 0) return DeltaTrend.Neutral;

  return DeltaTrend.Positive;
};

export const changeView = (
  value: number,
  unit: string,
  cellClassNames: string = "",
  invertedMarking = false,
) => (
  <span
    className={twMerge(
      "flex items-center justify-start text-sm text-black",
      cellClassNames,
    )}
  >
    <UnitDiff
      diff={value}
      unit={unit}
      trend={getChangeValue(invertedMarking ? -value : value)}
    />
  </span>
);

export const textView = (
  value: string | number,
  cellClassNames: string = "",
) => (
  <span
    className={twMerge(
      "flex items-center overflow-hidden px-3 text-start text-sm text-ellipsis whitespace-nowrap text-black",
      cellClassNames,
    )}
  >
    {value}
  </span>
);

export const listView = (
  values: string[] | number[],
  cellClassNames: string = "",
) => (
  <ul
    className={twMerge(
      "list-inside list-disc overflow-hidden px-3 py-4 text-start text-sm text-ellipsis whitespace-nowrap text-black",
      cellClassNames,
    )}
  >
    {values.map((value, index) => (
      <li key={index}>{value}</li>
    ))}
  </ul>
);

export const weightView = (value: number, cellClassNames: string = "") => (
  <span
    className={twMerge(
      "flex items-center justify-start px-3 text-sm text-black",
      cellClassNames,
    )}
  >
    {formatWeight(value, { suffix: " kg" })}
  </span>
);

export const titlePercentageView = (
  value: string,
  cellClassNames: string = "",
) => (
  <span
    className={twMerge(
      "flex items-center px-3 pr-8 text-start text-sm text-black",
      cellClassNames,
    )}
  >
    {formatNumberForTable(value, { suffix: " %" })}
  </span>
);

export const checkboxView = (
  value: boolean,
  rowData: { [k: string]: any },
  onChange?: onChangeType,
) => {
  return (
    <span
      className={twMerge(
        "flex items-center overflow-hidden px-3 text-start text-sm text-ellipsis whitespace-nowrap text-black",
      )}
    >
      <Checkbox
        isChecked={value}
        onChange={() => onChange?.(!value, rowData)}
      />
    </span>
  );
};

export const smartCapabilitiesView = (value: string) => {
  return textView(translate(`drawerLabels:smartCapabilities_${value}`));
};
